<template>
  <section class="practice-page">
    <header class="toolbar-card">
      <button type="button" class="back-btn" @click="goBack">{{ t("back") }}</button>
      <div>
        <p class="panel-tag">{{ t("eyebrow") }}</p>
        <h1 class="page-title">{{ t("title") }}</h1>
      </div>
    </header>

    <div v-if="loading" class="state-card">{{ t("loading") }}</div>
    <div v-else-if="!current" class="state-card">{{ t("noData") }}</div>
    <template v-else>
      <article class="question-card">
        <div class="progress-panel">
          <div class="progress-copy">
            <strong>{{ t("progress") }}</strong>
            <span>{{ currentIndex + 1 }} / {{ questions.length }}</span>
          </div>
          <div class="progress-track" aria-hidden="true">
            <div class="progress-fill" :style="{ width: `${progressPercent}%` }"></div>
          </div>
        </div>

        <div class="question-head">
          <p class="question-kicker">{{ t("questionNo") }} {{ currentIndex + 1 }}</p>
          <div class="word-audio">
            <strong>{{ current.wordText }}</strong>
            <span v-if="current.wordPhonetic">{{ current.wordPhonetic }}</span>
            <button type="button" class="speak-btn" @click="speakEnglish(current.wordText)">
              {{ t("speak") }}
            </button>
          </div>
        </div>

        <p class="question-body">{{ current.prompt }}</p>

        <div class="choice-list">
          <label v-for="(choice, index) in current.choices" :key="choice" class="choice-item">
            <input v-model="selectedAnswer" type="radio" :value="choice" />
            <span>{{ String.fromCharCode(65 + index) }}. {{ choice }}</span>
          </label>
        </div>

        <div class="action-row">
          <button type="button" class="action-btn" @click="checkAnswer">{{ t("confirm") }}</button>
        </div>

        <div class="result-box">
          <p v-if="resultState === 'ok'" class="ok">{{ t("correct") }}</p>
          <template v-else-if="resultState === 'ng'">
            <p class="ng">{{ t("wrongGuide") }}</p>
            <p v-if="current.questionType === 'root'" class="answer-summary">
              {{ t("rootMeaning") }}: {{ current.answer }}
            </p>
            <table v-else class="result-table">
              <thead>
                <tr>
                  <th>{{ t("item") }}</th>
                  <th v-for="part in current.parts" :key="part.index">{{ part.label }}</th>
                  <th>{{ t("word") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{{ t("structure") }}</td>
                  <td v-for="part in current.parts" :key="`s-${part.index}`">{{ part.text }}</td>
                  <td>{{ current.wordText }}</td>
                </tr>
                <tr>
                  <td>{{ t("literalMeaning") }}</td>
                  <td :colspan="current.parts.length"></td>
                  <td>{{ current.wordChar }}</td>
                </tr>
                <tr>
                  <td>{{ t("meaning") }}</td>
                  <td v-for="part in current.parts" :key="`m-${part.index}`">{{ part.meaning }}</td>
                  <td>{{ current.wordMeaning }}</td>
                </tr>
              </tbody>
            </table>
          </template>
        </div>
      </article>

      <div class="pager">
        <button type="button" @click="move(-1)" :disabled="currentIndex <= 0">{{ t("prev") }}</button>
        <button type="button" @click="move(1)" :disabled="currentIndex >= questions.length - 1">{{ t("next") }}</button>
      </div>
    </template>
  </section>
</template>

<script setup>
import axios from "axios";
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { useSiteLanguage } from "../../store/siteLanguage";
import {
  API_BASE,
  ensureApiArray,
  getPronunciation,
  getRootMeaning,
  getStructureParts,
  getWordChar,
  getWordMeaning,
  speakEnglish,
} from "./englishStudyShared";

const router = useRouter();
const STORAGE_CONFIG = "english_practice_config";
const STORAGE_INDEX = "english_practice_options_index";
const { lang } = useSiteLanguage();

const loading = ref(false);
const questions = ref([]);
const currentIndex = ref(0);
const selectedAnswer = ref("");
const resultState = ref("");

const current = computed(() => questions.value[currentIndex.value] || null);
const progressPercent = computed(() => {
  if (!questions.value.length) {
    return 0;
  }
  return ((currentIndex.value + 1) / questions.value.length) * 100;
});

const i18n = {
  ja: {
    back: "Back",
    eyebrow: "MULTIPLE CHOICE",
    title: "選択式練習",
    loading: "Loading...",
    noData: "練習に使える単語がありません。",
    progress: "進捗",
    questionNo: "Question",
    questionBodyRoot: "語根 {root} に最も近い意味を選んでください。",
    questionBody: "{word} の {label}「{part}」に最も近い意味を選んでください。",
    questionBodyChar: "{word} の字面意味に最も近いものを選んでください。",
    questionBodyWordMeaning: "{word} の単語全体の意味に最も近いものを選んでください。",
    speak: "Read",
    confirm: "答え合わせ",
    correct: "正解です。",
    wrongGuide: "少し違いました。構造と意味を下で確認してください。",
    item: "項目",
    word: "単語",
    rootMeaning: "語根意味",
    structure: "構造",
    literalMeaning: "字面意味",
    meaning: "意味",
    prev: "前へ",
    next: "次へ",
  },
  zh: {
    back: "Back",
    eyebrow: "MULTIPLE CHOICE",
    title: "选择题练习",
    loading: "Loading...",
    noData: "没有可用于练习的单词。",
    progress: "进度",
    questionNo: "题目",
    questionBodyRoot: "请为词根 {root} 选择最接近的含义。",
    questionBody: "请为 {word} 的 {label}「{part}」选择最接近的意义。",
    questionBodyChar: "请为 {word} 选择最接近它字面组合义的意思。",
    questionBodyWordMeaning: "请为 {word} 选择最接近它整词意思的含义。",
    speak: "朗读",
    confirm: "检查答案",
    correct: "回答正确。",
    wrongGuide: "这题答错了。请在下方查看结构和意义。",
    item: "项目",
    word: "单词",
    rootMeaning: "词根意思",
    structure: "结构",
    literalMeaning: "字面意思",
    meaning: "意义",
    prev: "上一题",
    next: "下一题",
  },
  en: {
    back: "Back",
    eyebrow: "MULTIPLE CHOICE",
    title: "Multiple-choice Practice",
    loading: "Loading...",
    noData: "No words available for practice.",
    progress: "Progress",
    questionNo: "Question",
    questionBodyRoot: "Choose the closest meaning for the root {root}.",
    questionBody: "Choose the closest meaning for {word}'s {label} \"{part}\".",
    questionBodyChar: "Choose the closest literal meaning for {word}.",
    questionBodyWordMeaning: "Choose the closest overall meaning for {word}.",
    speak: "Read",
    confirm: "Check Answer",
    correct: "Correct.",
    wrongGuide: "Not quite. Review the structure and meaning below.",
    item: "Item",
    word: "Word",
    rootMeaning: "Root Meaning",
    structure: "Structure",
    literalMeaning: "Literal Meaning",
    meaning: "Meaning",
    prev: "Previous",
    next: "Next",
  },
};

function t(key, params = null) {
  const text = i18n[lang.value]?.[key] || key;
  if (!params) {
    return text;
  }

  return text.replace(/\{(\w+)\}/g, (_, token) => String(params[token] ?? ""));
}

function shuffle(list) {
  const next = [...list];
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [next[i], next[j]] = [next[j], next[i]];
  }
  return next;
}

function buildChoices(answer, pool) {
  const distractors = shuffle(pool.filter((value) => value && value !== "-" && value !== answer)).slice(0, 3);
  return shuffle([answer, ...distractors]);
}

function buildQuestionBank(words, root) {
  const normalized = words
    .map((word) => {
      const parts = getStructureParts(word, lang.value).map((part) => ({
        ...part,
        meaning: lang.value === "zh" ? part.meaningZh : lang.value === "en" ? part.meaningEn : part.meaningJa,
      }));
      return {
        wordId: word.word_id,
        wordText: word.word_text,
        wordPhonetic: getPronunciation(word),
        wordChar: getWordChar(word, lang.value),
        wordMeaning: getWordMeaning(word, lang.value),
        parts,
      };
    })
    .filter((word) => word.parts.length > 0 || word.wordChar !== "-" || word.wordMeaning !== "-");

  const partPool = [...new Set(normalized.flatMap((word) => word.parts.map((part) => part.meaning)).filter(Boolean))];
  const charPool = [...new Set(normalized.map((word) => word.wordChar).filter((item) => item && item !== "-"))];
  const meaningPool = [...new Set(normalized.map((word) => word.wordMeaning).filter((item) => item && item !== "-"))];
  const distractorPool = [...new Set([...partPool, ...charPool, ...meaningPool].filter(Boolean))];
  const questions = [];

  const rootMeaning = root ? getRootMeaning(root, lang.value) : "";
  if (root && rootMeaning && rootMeaning !== "-") {
    questions.push({
      questionType: "root",
      wordText: root.root_text,
      wordPhonetic: "",
      wordChar: "-",
      wordMeaning: rootMeaning,
      parts: [],
      answer: rootMeaning,
      prompt: t("questionBodyRoot", { root: root.root_text }),
      choices: buildChoices(rootMeaning, distractorPool),
    });
  }

  for (const word of normalized) {
    for (const part of word.parts.filter((part) => part.type !== "r")) {
      questions.push({
        ...word,
        questionType: "part",
        answer: part.meaning,
        prompt: t("questionBody", { word: word.wordText, label: part.label, part: part.text }),
        choices: buildChoices(part.meaning, partPool),
      });
    }

    if (word.wordChar && word.wordChar !== "-") {
      questions.push({
        ...word,
        questionType: "char",
        answer: word.wordChar,
        prompt: t("questionBodyChar", { word: word.wordText }),
        choices: buildChoices(word.wordChar, charPool),
      });
    }

    if (word.wordMeaning && word.wordMeaning !== "-") {
      questions.push({
        ...word,
        questionType: "meaning",
        answer: word.wordMeaning,
        prompt: t("questionBodyWordMeaning", { word: word.wordText }),
        choices: buildChoices(word.wordMeaning, meaningPool),
      });
    }
  }

  return questions;
}

function matchRootFilter(word, keyword, selectedRootId) {
  const rootId = String(word.root_id || "").toLowerCase();
  const key = String(keyword || "").trim().toLowerCase();
  const selectedId = String(selectedRootId || "").trim().toLowerCase();
  if (selectedId && rootId === selectedId) {
    return true;
  }

  return Object.values(word.structure || {}).some((item) => String(item || "").toLowerCase() === key);
}

async function loadQuestions() {
  loading.value = true;
  try {
    const cfgRaw = localStorage.getItem(STORAGE_CONFIG);
    const cfg = cfgRaw ? JSON.parse(cfgRaw) : {};
    let keyword = cfg.useRootFilter ? String(cfg.rootKeyword || "").trim() : "";
    const selectedRootId = cfg.useRootFilter ? String(cfg.selectedRootId || "").trim() : "";
    if (!keyword) {
      keyword = "act";
    }

    const response = await axios.get(`${API_BASE}/api/word/search`, {
      params: keyword ? { keyword } : {},
      timeout: 10000,
    });
    const wordsRaw = ensureApiArray(response, "Word search API");
    const words = cfg.useRootFilter ? wordsRaw.filter((word) => matchRootFilter(word, keyword, selectedRootId)) : wordsRaw;
    let selectedRoot = null;
    if (cfg.useRootFilter) {
      const rootResponse = await axios.get(`${API_BASE}/api/root/search`, {
        params: selectedRootId ? { keyword: selectedRootId } : keyword ? { keyword } : {},
        timeout: 10000,
      });
      const roots = ensureApiArray(rootResponse, "Root search API");
      selectedRoot = roots.find((item) => String(item.root_id || "") === selectedRootId)
        || roots.find((item) => String(item.root_text || "").toLowerCase() === String(keyword || "").toLowerCase())
        || roots[0]
        || null;
    }

    questions.value = buildQuestionBank(words, selectedRoot);

    const shouldResume = String(localStorage.getItem("english_practice_resume")) === "true";
    const savedIndex = Number(localStorage.getItem(STORAGE_INDEX) || "0");
    currentIndex.value = shouldResume && Number.isFinite(savedIndex)
      ? Math.max(0, Math.min(savedIndex, Math.max(0, questions.value.length - 1)))
      : 0;
  } catch (error) {
    console.error("Failed to load multiple-choice practice:", error);
    questions.value = [];
  } finally {
    loading.value = false;
  }
}

function checkAnswer() {
  if (!current.value || !selectedAnswer.value) {
    return;
  }

  resultState.value = selectedAnswer.value === current.value.answer ? "ok" : "ng";
}

function move(step) {
  const next = currentIndex.value + step;
  if (next < 0 || next >= questions.value.length) {
    return;
  }

  currentIndex.value = next;
  selectedAnswer.value = "";
  resultState.value = "";
  localStorage.setItem(STORAGE_INDEX, String(next));
}

function goBack() {
  router.push("/english/practice");
}

onMounted(loadQuestions);
</script>

<style scoped>
.practice-page {
  width: 100%;
  min-height: calc(100vh - 86px);
  padding: 18px;
  box-sizing: border-box;
  background:
    radial-gradient(circle at top left, rgba(59, 130, 246, 0.14), transparent 30%),
    linear-gradient(180deg, #f5f9ff 0%, #eef6fb 100%);
  color: #163047;
}

.toolbar-card,
.question-card,
.state-card {
  border: 1px solid rgba(43, 90, 132, 0.14);
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 18px 50px rgba(34, 73, 112, 0.08);
}

.progress-panel {
  margin-bottom: 20px;
  padding: 14px 16px;
  border: 1px solid rgba(53, 100, 142, 0.14);
  border-radius: 20px;
  background: linear-gradient(180deg, rgba(233, 243, 255, 0.84), rgba(248, 251, 255, 0.96));
}

.progress-copy {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
  color: #234666;
  font-size: 14px;
}

.progress-copy strong {
  font-size: 15px;
  color: #15314c;
}

.progress-track {
  width: 100%;
  height: 10px;
  border-radius: 999px;
  background: rgba(54, 103, 149, 0.12);
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #2f7bc1 0%, #143d67 100%);
  transition: width 180ms ease;
}

.toolbar-card {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 18px;
  padding: 22px 24px;
  align-items: start;
}

.panel-tag {
  margin: 0 0 8px;
  font-size: 12px;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: #35648e;
}

.page-title {
  margin: 0;
  color: #10253a;
  font-size: clamp(28px, 4vw, 46px);
}

.back-btn,
.speak-btn,
.action-btn,
.pager button {
  cursor: pointer;
  transition: transform 180ms ease, box-shadow 180ms ease, background 180ms ease;
}

.back-btn,
.action-btn,
.pager button {
  border: 0;
  border-radius: 14px;
  padding: 12px 16px;
  font-weight: 700;
}

.back-btn {
  background: #dceaf8;
  color: #133251;
}

.state-card,
.question-card {
  margin-top: 18px;
  padding: 22px;
}

.question-kicker {
  margin: 0;
  color: #35648e;
  font-size: 12px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
}

.question-head {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: start;
  flex-wrap: wrap;
}

.word-audio {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.speak-btn,
.action-btn {
  background: linear-gradient(135deg, #0f4f8c, #1e7fc7);
  color: #fff;
}

.question-body {
  margin: 18px 0;
  font-size: 24px;
  color: #10253a;
}

.choice-list {
  display: grid;
  gap: 10px;
}

.choice-item {
  display: flex;
  gap: 10px;
  align-items: center;
  border: 1px solid rgba(43, 90, 132, 0.14);
  border-radius: 18px;
  background: #fbfdff;
  padding: 14px;
}

.action-row {
  margin-top: 16px;
}

.result-box {
  margin-top: 18px;
  border: 1px solid rgba(43, 90, 132, 0.14);
  border-radius: 22px;
  background: #f8fbff;
  padding: 16px;
}

.ok {
  margin: 0;
  color: #166534;
  font-weight: 700;
}

.ng {
  margin: 0 0 12px;
  color: #a16207;
  font-weight: 700;
}

.answer-summary {
  margin: 0;
  color: #17344f;
  font-weight: 600;
}

.result-table {
  width: 100%;
  border-collapse: collapse;
}

.result-table th,
.result-table td {
  border: 1px solid rgba(43, 90, 132, 0.18);
  padding: 8px 10px;
  text-align: left;
}

.pager {
  margin-top: 18px;
  display: flex;
  justify-content: center;
  gap: 10px;
}

.pager button {
  background: #dceaf8;
  color: #133251;
}

.back-btn:hover,
.speak-btn:hover,
.action-btn:hover,
.pager button:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 24px rgba(25, 79, 121, 0.12);
}

@media (max-width: 768px) {
  .practice-page {
    padding: 12px;
  }

  .toolbar-card,
  .question-card,
  .state-card {
    padding: 18px;
    border-radius: 22px;
  }

  .question-body {
    font-size: 20px;
  }
}
</style>
